import {call, delay, put, select, takeLatest} from 'redux-saga/effects';

//Actions
import * as actions from '../../actions';

//Services
import {ApiChangePasswordPayload, postChangePassword, postLogin, PostLoginResult, postPasswordRecovery, PostPasswordRecoveryResult} from '../../services/auth-api';

//Types
import * as Actions from '../../actions/actions';
import * as Payloads from '../../actions/payloads';
import {
    ConfirmMfaConfigResponse,
    GetMfaConfigResponse,
    MfaConfig,
    MfaStatus,
    SessionIdPayload
} from '../../actions/payloads';
import {AxiosResponse} from 'axios';
import {ActionType} from 'typesafe-actions';
import {getSessionData} from '../generic/saga';
import {getCustomerInfoByLogin} from '../myProfile/saga';
import JSONFormData from '../../../utils/JSONFormData';
import {AccessControl, Session} from '../../../services/endpoints';
import {api, clearStorageDatBeforeLogout, logOut, setLogoutErrorVisible} from '../../services/axios';
import {APIErrorInterface, ReduxState} from '../../types';
import i18n from '../../../services/i18n';
import {showToast} from "../../../utils/showToast";
import Config from '../../../config.json';
import { PasswordErrorsPartialText } from '../../../views/ResetPassword/ResetPassword.utils';

export const PASSWORD_EXPIRED_ERROR =
    'Server.Session.alert_You_must_change_password';

export const INVALID_VERIFICATION_CODE =
    'INVALID_VERIFICATION_CODE';

export const INVALID_VERIFICATION_CODE_MAX_ATTEMPTS_REACHED =
    'INVALID_VERIFICATION_CODE_MAX_ATTEMPTS_REACHED';

export const PASSWORD_CHANGE_MISSING_OTP_ERROR = 'Server.Session.change_password.one_time_password_missed';

export const AUTH_FAILED_ERROR = 'Server.Session.auth_failed';

export function* signIn(action: ActionType<typeof actions.logIn.request>) {
    setLogoutErrorVisible(false)
    const {login, password} = action.payload;

    localStorage.removeItem('user_info');
    localStorage.setItem('user_login', login);
    localStorage.setItem('user_login_page', 'true');

    let isAccountRealm = false;
    let res: PostLoginResult = yield postLogin({
        login: login.trim(),
        password: password.trim(),
        enable_csrf_protection: 1
    }, Config.CUSTOMER_API_URL);

    if(!res.success && res.apiError && res.apiError.faultcode == AUTH_FAILED_ERROR 
        && Config.ACCOUNT_API_URL && Config.ACCOUNT_API_URL.length > 0) {
        isAccountRealm = true;
        res = yield postLogin({
            login: login.trim(),
            password: password.trim(),
            enable_csrf_protection: 1
        }, Config.ACCOUNT_API_URL);
    }

    if(res.success && res.sessionResult)
    {
        if(isAccountRealm) {
            localStorage.setItem('user_info', JSON.stringify({
                realm: 'account'
            }));
        }

        if (res.sessionResult.mfa_enabled === 1 && res.sessionResult.mfa_verified === undefined) {
            yield call(generateMfaConfig,
                actions.generateMfaConfig.request(res.sessionResult));
            return;
        }
        if (res.sessionResult.mfa_enabled === 1 && res.sessionResult.mfa_verified === 0) {
            yield put(actions.twoFaValidationRequired(res.sessionResult));
            return;
        }

        yield call(continueDefaultLogin, res.sessionResult);
    } 
    else if(!res.success && res.apiError) {
        if (res.apiError.faultcode == PASSWORD_EXPIRED_ERROR) {

            if(isAccountRealm) {
                localStorage.setItem('user_info', JSON.stringify({
                    realm: 'account'
                }));
            }

            yield put(
                actions.logInPasswordExpired({
                    login: login,
                    password: password,
                }),
            );
        } else {
            //@ts-ignore
            yield put(actions.logIn.failure({response:{data:res.apiError}}));
        }
    }
}

export function* continueDefaultLogin(session: SessionIdPayload) {

    localStorage.setItem('session_id', session.session_id);
    localStorage.setItem('access_token', session.access_token);
    localStorage.setItem('csrf_token', session.csrf_token);
    localStorage.removeItem('default_portal_view_configuration');
    localStorage.removeItem('user_login_page');
    localStorage.removeItem('session_expired');

    yield put(
        actions.logIn.success(session),
    );
    yield call(getSessionData);
    yield call(getCustomerInfoByLogin);
}

export function* changePassword(
    action: Actions.PromiseAction<Payloads.LoginPayload &
        Payloads.PasswordPayload &
        Payloads.ChangePasswordPayload>,
) {
    const {login, password, new_password} = action.payload;

    try {
        const res: AxiosResponse<{
            success: number;
        }> = yield postChangePassword({
            login: login,
            password: password,
            new_password: new_password,
        });
        yield put(actions.passwordChange.success(res.data));
    } catch (err) {
        // @ts-ignore
        yield put(actions.passwordChange.failure(err));
    }
}

export function* changeExpiredPassword(
    action: Actions.PromiseAction<Payloads.LoginPayload &
        Payloads.PasswordPayload &
        Payloads.ChangePasswordPayload & ApiChangePasswordPayload>,
) {
    const {login, password, new_password, one_time_password} = action.payload;

    try {
        const res: AxiosResponse<{
            success: number;
        }> = yield postChangePassword({
            login: login,
            password: password,
            new_password: new_password,
            one_time_password: one_time_password
        });

        delay(1);

        yield call(signIn, actions.logIn.request({login: login, password: new_password}));

        if (one_time_password?.length) {
            delay(1);

            const {waiting2FA} = yield select(
                (state: ReduxState) => state.auth,
            );

            if (waiting2FA) {
                yield call(validate2FaCode, actions.validate2FaCode.request({
                    sessionId: waiting2FA,
                    one_time_password: one_time_password
                }));
            }
        }

        yield put(actions.changeExpiredPassword.success(res.data));

        if(action.payload.onSuccess)
        {
            action.payload.onSuccess();
        }

    } catch (err) {
        // @ts-ignore
        yield put(actions.changeExpiredPassword.failure(err));
    }
}

export function* passwordRecovery(
    action: ActionType<typeof actions.passwordRecovery.request>,
) {
    let res: PostPasswordRecoveryResult = yield postPasswordRecovery(action.payload, Config.CUSTOMER_API_URL);
    if(res.success) {
        yield put(actions.passwordRecovery.success({
            success: 1
        }));
    } else {
        const translationKey = getTranslationKeyByResetPasswordText(res.apiError);
        if(translationKey == 'anEmailWillBeSentIfEmailIsCorrect' 
            || translationKey == 'passwordResetLinkIsInvalidOrExpired') {

            if(Config.ACCOUNT_API_URL && Config.ACCOUNT_API_URL.length > 0) {
                res = yield postPasswordRecovery(action.payload, Config.ACCOUNT_API_URL);
            }
            if(res.success) {
                yield put(actions.passwordRecovery.success({
                    success: 1
                }));
            } else {
                if (res.apiError?.faultstring?.indexOf(PasswordErrorsPartialText.PasswordChangeFail) != -1) {
                    // @ts-ignore
                    yield put(actions.passwordRecovery.failure({...res.apiError}));
                }
                else
                {
                    const translationKey1 = getTranslationKeyByResetPasswordText(res.apiError);
                    // @ts-ignore
                    yield put(actions.passwordRecovery.failure({...res.apiError, translationKey: translationKey1}));
                }
            }
        }
        else
        {
            if (res.apiError?.faultstring?.indexOf(PasswordErrorsPartialText.PasswordChangeFail) != -1) {
                // @ts-ignore
                yield put(actions.passwordRecovery.failure({...res.apiError}));
            }
            else
            {
                // @ts-ignore
                yield put(actions.passwordRecovery.failure({...res.apiError, translationKey: translationKey}));
            }
        }
    }
}

function getTranslationKeyByResetPasswordText (apiError?: APIErrorInterface) {
    let translationKey: string | undefined = 'anEmailWillBeSentIfEmailIsCorrect';
    const errorDetails = (apiError?.faultstring ?? '').replaceAll('An error occurred:', '').trim();

    switch(errorDetails)
    {
        case 'No such user.':
            translationKey = 'anEmailWillBeSentIfEmailIsCorrect';
            break;
        case 'Requested nonce not found.':
            translationKey = 'passwordResetLinkIsInvalidOrExpired';
            break;
        case 'Password has not been changed.':
            translationKey = 'passwordHasNotBeenChanged';
            break;
        case 'Notification for password change is disabled':
        case 'Notification was not sent.':
            translationKey = 'notificationWasNotSent';
            break;
        default:
            translationKey = 'resetPasswordFallback';
            break;
    }
    return translationKey;
}

export function* emailMePasswordRecovery(
    action: Actions.PromiseAction<Payloads.MailMePayload>,
) {
    let result: PostPasswordRecoveryResult = yield postPasswordRecovery(action.payload, Config.CUSTOMER_API_URL);

    if (result.success) {
        action.payload.callback?.();
        
        yield put(actions.sendRecoveryPasswordEmail.success({
            success: 1
        }));
    }
    else {
        const translationKey = getTranslationKeyByResetPasswordText(result.apiError);
        if(translationKey == 'anEmailWillBeSentIfEmailIsCorrect' 
            || translationKey == 'passwordResetLinkIsInvalidOrExpired') {

            if(Config.ACCOUNT_API_URL && Config.ACCOUNT_API_URL.length > 0) {
                result = yield postPasswordRecovery(action.payload, Config.ACCOUNT_API_URL);
            }
            
            if (result.success) {
                action.payload.callback?.();
            
                yield put(actions.sendRecoveryPasswordEmail.success({
                    success: 1
                }));
            }
            else {
                const translationKey1 = getTranslationKeyByResetPasswordText(result.apiError);
                // @ts-ignore
                yield put(actions.sendRecoveryPasswordEmail.failure({...result.apiError, translationKey: translationKey1}));
            }
        }
        else
        {
            // @ts-ignore
            yield put(actions.sendRecoveryPasswordEmail.failure({...result.apiError, translationKey: translationKey}));
        }
    }
}

export function* signInFromAdmin(action: ActionType<typeof actions.signInFromAdmin.request>) {
    try {
        const {value} = action.payload;
        const data = JSON.parse(Buffer.from(value, "base64").toString());
        const session_id = data.pb_session_id;
        const token = data.access_token;

        yield put(actions.clearStorageData());

        localStorage.setItem('session_id', session_id);
        localStorage.setItem('access_token', token);
        localStorage.setItem('csrf_token', data.csrf_token);
        localStorage.setItem('default_portal_view_configuration', data.i_portal_view_configuration);
        yield put(
            actions.logIn.success({
                session_id: session_id,
                access_token: token,
                csrf_token: data.csrf_token
            }),
        );

        yield call(getSessionData);
        yield call(getCustomerInfoByLogin);
    } catch (err) {
        // @ts-ignore
        yield put(actions.logIn.failure(err));
    }
}

export function clearStorageData()
{
    clearStorageDatBeforeLogout();
}

export function* removeAuthDataAndReload(action: ActionType<typeof actions.removeAuthDataAndReload>) {
    try {
        yield call(removeApiSession)
    } catch (e) {
        // Session may already be terminated — ignore
    }
    logOut(action.payload.waitTime || 3000, action.payload?.reload)
}

export function* removeApiSession() {

    const {session_id, csrf_token} = yield select(
        (state: ReduxState) => state.auth,
    );

    const body = new JSONFormData(session_id, csrf_token);
    yield api.post(Session.Logout, body);

}

export function* generateMfaConfig(action: ActionType<typeof actions.generateMfaConfig.request>) {
    try {
        const body = new JSONFormData(action.payload.session_id, action.payload.csrf_token);

        const response: AxiosResponse<MfaConfig> = yield api.post(AccessControl.GenerateMfaConfig, body);

        const config: MfaConfig = {
            ...response.data,
            sessionId: action.payload
        };
        yield put(actions.generateMfaConfig.success(config));
    } catch (err) {
        // @ts-ignore
        yield put(actions.generateMfaConfig.failure(err));
    }
}

export function* confirmMfaConfig(action: ActionType<typeof actions.confirmMfaConfig.request>) {
    try {
        const body = new JSONFormData(action.payload.sessionId.session_id, action.payload.sessionId.csrf_token);

        body.setParams({
            ...action.payload,
            sessionId: undefined
        });
        const response: AxiosResponse<ConfirmMfaConfigResponse> = yield api.post(AccessControl.ConfirmMfaConfig, body);

        if (response.data?.success) {
            yield put(actions.confirmMfaConfig.success(response.data));
            if (action.payload.sessionId.mfa_enabled !== -1) {
                yield call(continueDefaultLogin, action.payload.sessionId);
            }
            action.payload.onSuccess?.();
        } else {
            const obj: APIErrorInterface = {
                faultcode: INVALID_VERIFICATION_CODE,
                faultstring: i18n.t<string>('screens:twoFa.incorrectCode')
            };
            yield put(actions.confirmMfaConfig.failure(obj));
        }
    } catch (err) {
        // @ts-ignore
        yield put(actions.confirmMfaConfig.failure(err));
    }
}

export function* validate2FaCode(action: ActionType<typeof actions.validate2FaCode.request>) {

    try {
        const body = new JSONFormData(action.payload.sessionId.session_id, action.payload.sessionId.csrf_token);

        body.setParams({
            ...action.payload,
            sessionId: undefined
        });
        const response: AxiosResponse<ConfirmMfaConfigResponse> = yield api.post(AccessControl.VerifyOtp, body);

        const invalidCodeObject: APIErrorInterface = {
            faultcode: INVALID_VERIFICATION_CODE,
            faultstring: i18n.t<string>('screens:twoFa.incorrectCode')
        };

        if (response.data?.success) {
            yield put(actions.validate2FaCode.success(response.data));
            yield call(continueDefaultLogin, action.payload.sessionId);
        } else {
            const leftAttempts = (response.data?.mfa_attempts || -1);
            if (leftAttempts >= 0) {
                invalidCodeObject.faultstring = i18n.t<string>(leftAttempts === 1
                    ? 'screens:twoFa.incorrectCodeAttemptCount'
                    : 'screens:twoFa.incorrectCodeAttemptsCount', {
                    value: response.data?.mfa_attempts?.toString()
                });
            }
            //incorrectCodeAttemptCount
            if (leftAttempts === 0) {
                invalidCodeObject.faultcode = INVALID_VERIFICATION_CODE_MAX_ATTEMPTS_REACHED;
            }
            yield put(actions.validate2FaCode.failure(invalidCodeObject));
        }
    } catch (err) {
        const invalidCodeObject: APIErrorInterface = {
            faultcode: INVALID_VERIFICATION_CODE_MAX_ATTEMPTS_REACHED,
            faultstring: i18n.t<string>('screens:twoFa.incorrectCode')
        };
        // @ts-ignore
        yield put(actions.validate2FaCode.failure(invalidCodeObject));
    }
}

export function* getMfaConfig(action: ActionType<typeof actions.getMfaConfig.request>) {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        const response: AxiosResponse<GetMfaConfigResponse> = yield api.post(AccessControl.GetMfaConfig, body);
        yield put(actions.getMfaConfig.success(response.data));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
        let error: APIErrorInterface = err?.response?.data || err?.data;
        if (!error?.faultcode?.length) {
            error = {
                faultcode: 'Server.AccessControl.get_mfa_config.not_exists',
                faultstring: "Can't access 'get_mfa_config' method in AccessControl"
            };
        }
        yield put(actions.getMfaConfig.failure(error));
    }
}

export function* updateMfaUsed(action: ActionType<typeof actions.updateMfaUsed.request>) {
    try {
        const {session_id, csrf_token} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        const response: AxiosResponse<ConfirmMfaConfigResponse> = yield api.post(AccessControl.SetMfaConfig, body);

        if ((response.data.success || 0) > 0) {
            if (action.payload.use_mfa === MfaStatus.Disabled) {
                showToast(i18n.t<string>('screens:twoFa.successDisabledNotification'));
            }

            const setupConfigure = action.payload.mfa_configured !== undefined && action.payload.mfa_configured !== null;
            const obj: GetMfaConfigResponse = {
                mfa_used: action.payload.use_mfa,
                mfa_configured: setupConfigure ? (action.payload.mfa_configured || 0) : (action.payload.use_mfa === MfaStatus.Enabled ? 1 : 0),
                effective_mfa_used: action.payload.use_mfa,
                object: action.payload.object,
                i_object: action.payload.i_object,
            };
            yield put(actions.updateMfaUsed.success(obj));
            action.payload.onSuccess?.();
        } else {
            yield put(actions.updateMfaUsed.failure(response.data));
        }
    } catch (err) {
        yield put(actions.updateMfaUsed.failure({
            success: 0,
            mfa_attempts: 0
        }));
    }
}

export function* resetMfa(action: ActionType<typeof actions.resetMfa.request>) {
    try {
        const {session_id, csrf_token, individualMfaConfig} = yield select(
            (state: ReduxState) => state.auth,
        );

        const body = new JSONFormData(session_id, csrf_token);
        body.setParams(action.payload);

        const response: AxiosResponse<ConfirmMfaConfigResponse> = yield api.post(AccessControl.ResetMfaConfig, body);

        const obj: GetMfaConfigResponse = {
            ...individualMfaConfig,
            mfa_configured: action.payload.one_time_password?.length ? 1 : 0
        };

        if ((response.data.success || 0) > 0) {
            yield put(actions.resetMfa.success(obj));
            showToast(i18n.t<string>('screens:twoFa.successResetNotification'));
            action.payload.onSuccess?.();
        } else {
            yield put(actions.resetMfa.failure(response.data));
        }
    } catch (err) {
        yield put(actions.resetMfa.failure({
            success: 0,
            mfa_attempts: 0
        }));
    }
}

export const authSaga = [
    takeLatest(actions.logIn.request, signIn),
    takeLatest(
        actions.sendRecoveryPasswordEmail.request,
        emailMePasswordRecovery,
    ),
    takeLatest(actions.passwordChange.request, changePassword),
    takeLatest(actions.changeExpiredPassword.request, changeExpiredPassword),
    takeLatest(actions.passwordRecovery.request, passwordRecovery),
    takeLatest(actions.signInFromAdmin.request, signInFromAdmin),
    takeLatest(actions.removeAuthDataAndReload, removeAuthDataAndReload),
    takeLatest(actions.clearStorageData, clearStorageData),
    takeLatest(actions.apiLogout, removeApiSession),
    takeLatest(actions.generateMfaConfig.request, generateMfaConfig),
    takeLatest(actions.confirmMfaConfig.request, confirmMfaConfig),
    takeLatest(actions.validate2FaCode.request, validate2FaCode),
    takeLatest(actions.getMfaConfig.request, getMfaConfig),
    takeLatest(actions.updateMfaUsed.request, updateMfaUsed),
    takeLatest(actions.resetMfa.request, resetMfa),
];
